import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import LovRiskStrategies from "src/components/Lov/RiskStrategies";
import Modal from "src/components/Modal";
import Select from "src/components/Select";
import Tabs from "src/components/Tabs";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ListOfActions, { IRefListOfActions } from "src/pages/Actions/Panel/listOfActions";
import IFiltersActionsPanel, { ActionGroupers, IRefActionsPanelModalAddChangeDelete } from "src/pages/Actions/Panel/interface";
import ModalAddChangeDelete from "src/pages/Actions/Panel/components/modalAddChangeDelete";
import ModalSide from "src/components/Modal/styles";
import { CheckImpact, CheckProbability, IAnalysis, IComponentRisksPanelModal, IRefModalAnalysis, IRisks } from "../interface";
import { registerAnalysisSchema } from "../schemas";

const ModalRiskAnalysis: React.ForwardRefRenderFunction<IRefModalAnalysis,IComponentRisksPanelModal> = ({
	onCallbackUpdate
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const [isAdd, setIsAdd] = useState(true);
	const [registered, setRegistered] = useState(false);
	const [riskData, setRiskData] = useState<IRisks>({} as IRisks);
	const [analysis, setAnalysis] = useState<IAnalysis>({} as IAnalysis);
	const [riskAnalysisInsertedId, setRiskAnalysisInsertedId] = useState(0);

	const modalAnalysis = useRef<RCCRefModalProps>(null);
	const listOfActionsRef = useRef<IRefListOfActions>(null);
	const modalsRef = useRef<IRefActionsPanelModalAddChangeDelete>(null);

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange
	}));

	const { handleSubmit, 
		register, 
		reset, 
		setError, 
		control,
		formState: { errors }, 
		clearErrors 
	} = useForm();

	const actionButtonAdd = (row: IRisks) => {
		reset();
		setRiskAnalysisInsertedId(0);
		setIsAdd(true);
		setRegistered(false);
		setRiskData(row); 
		setLoading(false);
		modalAnalysis.current?.openModal();
	};

	const actionButtonChange = (row: IRisks, analysisId: number) => {
		reset();
		setIsAdd(false);
		setRegistered(false);
		setLoading(false);
		getRiskAnalysis(analysisId);
		setRiskData(row);
	};

	const getRiskAnalysis = async (id: number) => {
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "RisksAnalysis/Select",
				urlParameters: {
					id
				}
			}); 

			setAnalysis(data);
			setLoading(false);

			modalAnalysis.current?.openModal();
			
			setRiskAnalysisInsertedId(data.rib003Id);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar o análise de risco"), addToast);
			setLoading(false);
		}
	};

	const submit = async (data: any) => {
		try{
			clearErrors();
			modalAnalysis.current?.setLoadingButton();

			const dataChanged = data;
			dataChanged.rib003Id = analysis.rib003Id;
			dataChanged.rib003Impacto =  data.rib003Impacto ? parseInt(data.rib003Impacto, 10): "";
			dataChanged.rib003Probabilidade = data.rib003Probabilidade? parseInt(data.rib003Probabilidade, 10) : "";
			dataChanged.rib003Ria002Titulo = data.rib003Ria002Id ? data.rib003Ria002Id.label : "";
			dataChanged.rib003Ria002Id = data.rib003Ria002Id ? parseInt(data.rib003Ria002Id.value, 10) : "";
			
			await registerAnalysisSchema(translate).validate(dataChanged,{
				abortEarly: false
			});

			if(isAdd){
				const response = await RCCServicesApi.postRequest({
					method: "RisksAnalysis/Insert",
					body: {
						...dataChanged,
						rib003Rib001Id: riskData.rib001Id
					}
				});
				
				setRiskAnalysisInsertedId(response.data);
	
				addToast({
					color: "green",
					description:  translate("Análise adicionada com sucesso"),
					title: translate("Sucesso")
				});

				
				setRegistered(true);

				if(onCallbackUpdate){
					onCallbackUpdate(riskData.rib001Id);
				}
			}else{
				await RCCServicesApi.putRequest({
					method: "RisksAnalysis/Update",
					body: {
						...dataChanged,
						rib003Rib001Id: riskData.rib001Id
					}
				});

				if(onCallbackUpdate)
					onCallbackUpdate(dataChanged);

				addToast({
					color: "green",
					description:  translate("Análise alterada com sucesso"),
					title: translate("Sucesso")
				});

				modalAnalysis.current?.closeModal();
			}
			modalAnalysis.current?.resetLoadingButton();
		}catch(err){
			modalAnalysis.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;
			standardErrorMessages(err, translate("Erro ao adicionar análise"), addToast);
		}
	};

	const defaultValueProbability = () => {
		if(isAdd){
			return riskData.rib001Probabilidade ? riskData.rib001Probabilidade.toString() : CheckProbability.VERY_LOW.toString();
		}

		return analysis.rib003Probabilidade ? analysis.rib003Probabilidade.toString() : undefined;
	};

	const defaultValueImpact = () => {
		if(isAdd){
			return riskData.rib001Impacto ? riskData.rib001Impacto.toString() : CheckImpact.VERY_LOW.toString();
		}

		return analysis.rib003Impacto ? analysis.rib003Impacto.toString() : undefined;
	};

	useEffect(() => {
		if(!riskAnalysisInsertedId)
			return;

		listOfActionsRef.current?.executeQuery();
	}, [riskAnalysisInsertedId]);

	return (
		<>
			<Modal
				withoutForm
				ref={modalAnalysis}
				header={{
					title: isAdd ? translate("Adicionar análise") : translate("Alterar análise"),
					icon: {
						name: isAdd ? "plus" : "pencil"
					},
					subtitle: `${translate("Risco")}: ${riskData.fullTitle}`
				}}
				onSubmit={handleSubmit(submit)}
				footer={{
					showSaveButton: !registered,
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				styleInline={ModalSide}
			>
				<Tabs 
					name="tabHistoric"
					tabInit={0}
					options={[
						{
							label: translate("Geral"),
							content: (
								<>
									<RCCGrid
										container
										item
										direction="column"
										alignItems="center"
									>
										<RCCGrid
											container
											item
											
											alignItems="center"
											justifyContent="flex-end"
										>
											<RCCGrid item>
												<Audit data={analysis} type="pop" position="left"/> 
											</RCCGrid>
										</RCCGrid>
										<RCCGrid
											container
											item		
										>
											<RCCTextArea 
												register={register}
												name="rib003Descricao"
												defaultValue={!isAdd ? analysis.rib003Descricao : ""}
												label={translate("Descrição")}
												maxLength={4000}
												error={errors.rib003Descricao}
												required
												disabled={registered}
											/>
										</RCCGrid>
										<RCCGrid
											container
											item	
										>
											<Select 
												control={control}
												name="rib003Probabilidade" 
												options={[
													{
														value: CheckProbability.VERY_HIGH.toString(),
														label: translate("Muito alto")
													},{
														value: CheckProbability.HIGH.toString(),
														label: translate("Alto")
													},
													{
														value: CheckProbability.MODERATE.toString(),
														label: translate("Moderado")
													},
													{
														value: CheckProbability.LOW.toString(),
														label: translate("Baixo")
													},{
														value: CheckProbability.VERY_LOW.toString(),
														label: translate("Muito baixo")
													}
												]} 
												label={translate("Probabilidade")}
												submit="value"
												error={errors.rib003Probabilidade}
												defaultValue={defaultValueProbability()}
												disabled={registered}
												required
											/>
										</RCCGrid>
										<RCCGrid
											container
											item	
										>	
											<Select 
												control={control}
												name="rib003Impacto" 
												options={[	
													{
														value: CheckImpact.VERY_HIGH.toString(),
														label: translate("Muito alto")
													},{
														value: CheckImpact.HIGH.toString(),
														label: translate("Alto")
													},
													{
														value: CheckImpact.MODERATE.toString(),
														label: translate("Moderado")
													},
													{
														value: CheckImpact.LOW.toString(),
														label: translate("Baixo")
													},{
														value: CheckImpact.VERY_LOW.toString(),
														label: translate("Muito baixo")
													}
												]} 
												label={translate("Impacto")}
												defaultValue={defaultValueImpact()}
												required
												submit="value"
												disabled={registered}
												error={errors.rib003Impacto}
											/>
										</RCCGrid>
										<RCCGrid
											container
											item	
										>
											<LovRiskStrategies 
												control={control}
												name="rib003Ria002Id"
												label={translate("Estratégia")}
												defaultValue={!isAdd ? { value: String(analysis.rib003Ria002Id), label: analysis.rib003Ria002Titulo } : undefined}
												extraParametersOnLoad={{
													Type: riskData.rib001Quadrante
												}}
												error={errors.rib003Ria002Id}
												disabled={registered}
												submit="value and label"
												required
											/>
										</RCCGrid>	

										{
											isAdd && 
											<RCCGrid
												container
												item	
												style={{ marginTop: "20px" }}
											>
												<RCCAlert 
													title={translate("Atenção")}
													description={translate("Após salvar a Análise, será liberada a opção 'Ações' onde você poderá adicionar/editar as ações desta Análise.")}
												/>
											</RCCGrid>
										}
									</RCCGrid>
								</>
							)
						},
						{
							label: translate("Ações"),
							hidden: riskAnalysisInsertedId === 0,
							content: (
								<ListOfActions 
									ref={listOfActionsRef}
									filters={{} as IFiltersActionsPanel}
									groupersSelect={{
										group: ActionGroupers.RISK_ANALYSIS,
										delayed: "",
										origin: ""
									}}
									groupers={{
										grouperId: riskAnalysisInsertedId
									}}
									layoutList="card"
									positionOptions="bottomEnd"
									header={{
										showTotal: true,
										button: <RCCTooltip
											text={translate("Nova ação")}
											color="purple"
											position="leftEnd"
										>
											<RCCButton
												icon={{name: "plus", color: "#FFF", size: 18}}
												color="purple"
												layout="isRounded"
												onClick={() => { if(!modalsRef.current) return; modalsRef.current.openModalAddOrChange(undefined, () => listOfActionsRef.current?.executeQuery(), undefined, undefined, {riskAnalysis: riskAnalysisInsertedId}); }}
												size="md"
											/>
										</RCCTooltip>
									}}
								/>
							)
						}
					]}
				/>
			</Modal>

			{/* Modal ADD inside risk analysis */}
			<ModalAddChangeDelete ref={modalsRef} />
		</>
	);
};

export default forwardRef(ModalRiskAnalysis);
